/* eslint-disable react/prop-types */
import React, { useState, useEffect } from 'react';
import { t } from 'i18next';
import PropTypes from 'prop-types';
import { useTable, useSortBy, useExpanded } from 'react-table';
import {
  TableContainer,
  CellContainer,
  FiltersAndTableContainer,
  ReversedIcon,
  StyledTable,
  TableDataCell,
  TableHeader,
  TableBody,
  TableRow,
  TableViewAllButtonWrapper,
} from './css';
import ChevronSmallDown from '../../../assets/icons/ChevronSmallDown.svg';
import {
  ExpandCellComponent, OrderStatusCellComponent, ActionsDropdown, TimeCellComponent, NameAndLocation, BooleanCol,
} from './cells';
import TertiaryButton from '../buttons/TertiaryButton';
import renderExpandables from './methods';
import { routes } from '../../../utils/constants';
import { ShipmentStatuses } from '../../../utils/enums';

const Table = (props) => {
  const [data, setData] = useState([]);
  const [dataExpander, setDataExpander] = useState([]);
  const [showViewAll, setShowViewAll] = useState(false);
  const {
    columns, datasource, viewAll, scrollbarheight,
  } = props;

  useEffect(() => {
    window.setTimeout(() => setShowViewAll(true), 1000);
  });

  useEffect(() => {
    setData(datasource);
  }, [datasource]);

  const shipmentsRowClickHandler = (orderId, status, column) => {
    const { isShipments } = props;

    let shouldOpen = true;

    if (status === ShipmentStatuses.Expired || status === ShipmentStatuses.UnderReview || status === ShipmentStatuses.NotYetPickedUp) {
      shouldOpen = false;
    }

    if (column === 'actions' || column === 'expandableTracking') {
      shouldOpen = false;
    }

    if (shouldOpen && isShipments) {
      window.open(`${routes.viewOrder}?probillNumber=${orderId}`);
    }
  };

  const theColumns = columns.map((col) => {
    if (col.type === 'actions') {
      return (
        {
          Header: col.header,
          accessor: col.accessor,
          columnType: col.type,
          disableSortBy: true,
          width: 100,
          Cell: (props) => ActionsDropdown({ data: props.data[props.row.index], options: col.params.options }),
        }
      );
    }

    if (col.type === 'datetime') {
      return (
        {
          Header: col.header,
          accessor: col.accessor,
          columnType: col.type,
          width: 120,
          Cell: (props) => TimeCellComponent({ time: props.data[props.row.index][col.accessor] }),
        }
      );
    }

    if (col.type === 'stts') {
      return (
        {
          Header: col.header,
          accessor: col.accessor,
          columnType: col.type,
          width: 175,
          Cell: (props) => OrderStatusCellComponent({
            percentage: props.data[props.row.index][col.params.percentage],
            status: props.data[props.row.index][col.params.status],
            color: props.data[props.row.index][col.params.color],
          }),
        }
      );
    }

    if (col.type === 'name-and-location') {
      return (
        {
          Header: col.header,
          accessor: col.accessor,
          columnType: col.type,
          width: (col.params.width) ? col.params.width : 200,
          Cell: (props) => NameAndLocation({ data: props.data[props.row.index][col.params.obj] }),
        }
      );
    }

    if (col.type === 'boolean') {
      return (
        {
          Header: col.header,
          accessor: col.accessor,
          columnType: col.type,
          width: (col.params.width) ? col.params.width : 100,
          Cell: (props) => BooleanCol({ data: props.data[props.row.index], params: col.params }),
        }
      );
    }

    if (col.type === 'expandableTracking') {
      return (
        {
          Header: '',
          accessor: col.accessor,
          disableSortBy: true,
          columnType: col.type,
          // eslint-disable-next-line react/no-unstable-nested-components
          Cell: ({ row }) => (
            <span {...row.getToggleRowExpandedProps({})}>
              {ExpandCellComponent({ reverse: row.isExpanded })}
            </span>
          ),
        }
      );
    }

    return ({
      Header: col.header,
      columnType: col.type,
      accessor: col.accessor,
    });
  });

  const tableColumns = React.useMemo(() => theColumns, []);
  const tableData = React.useMemo(() => data, [data]);

  const theTable = useTable({ columns: tableColumns, data: tableData }, useSortBy, useExpanded);

  const expandableCellTypes = ['expandableTracking'];

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow,
    visibleColumns,
  } = theTable;

  const { isShipments } = props;

  if (data.length > 0) {
    return (
      <TableContainer scrollbarheight={scrollbarheight}>
        <div style={{ width: 'max-content', minWidth: '100%' }}>
          <FiltersAndTableContainer>
            <StyledTable defaultColumn={{ width: 'auto' }} {...getTableProps()}>
              <TableHeader>
                {headerGroups.map((headerGroup) => (
                  <tr key={headerGroup} {...headerGroup.getHeaderGroupProps()}>
                    {headerGroup.headers.map((column, idx) => (
                      <th
                        key={column}
                        className={(idx < 2) ? `sticky table-header column-${idx}` : ''}
                        width={column.width}
                        {...column.getHeaderProps(column.getSortByToggleProps())}
                      >

                        {column.render('Header')}
                        <span>
                          {column.isSorted
                            ? column.isSortedDesc
                              ? <img src={ChevronSmallDown} alt="sort descending" />
                              : <ReversedIcon src={ChevronSmallDown} alt="sort ascending" />
                            : ''}
                        </span>

                      </th>
                    ))}
                  </tr>
                ))}
              </TableHeader>
              <TableBody {...getTableBodyProps()} isShipments={isShipments}>
                {rows.map((row) => {
                  prepareRow(row);
                  let expanderType = null;
                  const { isApproved } = row.original;

                  return (
                    <React.Fragment key={row.id}>

                      <tr {...row.getRowProps()}>
                        {row.cells.map((cell, idx) => {
                          const isExpander = (expandableCellTypes.indexOf(cell.column.columnType) > -1);

                          if (isExpander) {
                            expanderType = cell.column.columnType;
                          }

                          let orderId = null;
                          let orderStts = null;

                          if (isShipments) {
                            orderId = cell.row.original.orderId;
                            orderStts = cell.row.original.status;
                          }

                          return (
                            <TableDataCell
                              topAligned
                              key={cell}
                              className={(idx < 2) ? `sticky table-cell column-${idx}` : ''}
                              width={cell.column.width}
                              {...cell.getCellProps()}
                            >
                              <CellContainer onClick={() => shipmentsRowClickHandler(orderId, orderStts, cell.column.columnType)}>
                                {!isShipments && !isApproved && cell.column.id === 'status' ? t('StatusPENDING') : cell.render('Cell') }
                              </CellContainer>

                            </TableDataCell>
                          );
                        })}
                      </tr>

                      {
                        (row.isExpanded)
                          ? (
                            <TableRow>
                              <td colSpan={visibleColumns.length}>
                                {renderExpandables(expanderType, row, dataExpander, setDataExpander, data[row.index])}
                              </td>
                            </TableRow>
                          )
                          : null
                      }
                    </React.Fragment>
                  );
                })}

                {(viewAll && showViewAll)
                  ? (
                    <tr>
                      <td colSpan={9}>
                        <TableViewAllButtonWrapper>
                          <TertiaryButton
                            label={t('ViewAll')}
                            onClick={viewAll}
                            size="small"
                            showArrow
                          />
                        </TableViewAllButtonWrapper>
                      </td>
                    </tr>
                  )
                  : null}

              </TableBody>

            </StyledTable>

          </FiltersAndTableContainer>
        </div>
      </TableContainer>
    );
  }

  return null;
};

Table.propTypes = {
  columns: PropTypes.arrayOf(PropTypes.shape({
    header: PropTypes.string,
    accessor: PropTypes.string,
    type: PropTypes.string,
    params: PropTypes.object,
  })),
  datasource: PropTypes.arrayOf(PropTypes.object),
  viewAll: PropTypes.func,
  scrollbarheight: PropTypes.string,
  isShipments: PropTypes.bool,
};

export default Table;
